import { useAgGridDefaults } from "@/lib/ag-grid-defaults";
import { useServerGrid } from "@/lib/use-server-grid";
import { useWorkspacePage } from "@/components/workspace/use-workspace-page";
import {
	useAcknowledgeException,
	useDismissException,
	useResolveException,
} from "@/hooks/use-manufacturing";
import { manufacturingClient } from "@/lib/manufacturing-client";
import { AgGridReact } from "@ag-grid-community/react";
import type { ColDef, ICellRendererParams } from "@ag-grid-community/core";
import type { PlanningException } from "@/gen/omnios/v1/manufacturing_pb";
import { createFileRoute } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export const Route = createFileRoute("/manufacturing/exceptions")({
	component: ExceptionsPage,
});

// ── Severity cell renderer ─────────────────────────────────

function SeverityCell({ value }: ICellRendererParams) {
	const color =
		value === "critical"
			? "bg-red-500"
			: value === "high"
				? "bg-amber-500"
				: value === "medium"
					? "bg-blue-400"
					: "bg-gray-400";
	return (
		<span className="flex items-center gap-1.5">
			<span className={`h-2 w-2 rounded-full ${color}`} />
			<span className="capitalize">{value}</span>
		</span>
	);
}

function StatusCell({ value }: ICellRendererParams) {
	const variant =
		value === "open"
			? "destructive"
			: value === "acknowledged"
				? "secondary"
				: "outline";
	return <Badge variant={variant}>{value}</Badge>;
}

// ── Page component ─────────────────────────────────────────

function ExceptionsPage() {
	const { t } = useTranslation("manufacturing");
	const gridDefaults = useAgGridDefaults();
	const acknowledgeException = useAcknowledgeException();
	const resolveException = useResolveException();
	const dismissException = useDismissException();
	const [selectedIds, setSelectedIds] = useState<string[]>([]);

	const columns = useMemo<ColDef[]>(
		() => [
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 40,
				pinned: "left",
				sortable: false,
				filter: false,
				resizable: false,
			},
			{
				field: "severity",
				headerName: t("field_severity"),
				width: 120,
				cellRenderer: SeverityCell,
				filter: false,
				pinned: "left",
			},
			{
				field: "exceptionType",
				headerName: t("planning_exception"),
				width: 160,
			},
			{
				field: "productId",
				headerName: t("field_product"),
				width: 130,
				valueFormatter: (p: { value: string }) => p.value?.slice(0, 8) ?? "",
			},
			{
				field: "message",
				headerName: t("field_message"),
				flex: 1,
				minWidth: 200,
			},
			{
				field: "status",
				headerName: t("field_status"),
				width: 130,
				cellRenderer: StatusCell,
				filter: false,
			},
			{
				field: "createdAt",
				headerName: t("field_date"),
				width: 140,
				filter: "agDateColumnFilter",
				valueFormatter: (p: { value: { seconds?: bigint } }) => {
					if (!p.value?.seconds) return "";
					return new Date(
						Number(p.value.seconds) * 1000,
					).toLocaleDateString("ru-RU");
				},
			},
		],
		[t],
	);

	const { datasource, totalRows, refreshGrid, gridRef } = useServerGrid<
		PlanningException & { id: string }
	>({
		fetchRows: async ({ pageSize, pageToken }) => {
			const resp = await manufacturingClient.listExceptions({
				page: { pageSize, pageToken },
				severity: "",
				status: "",
				productId: "",
			});
			return {
				rows: resp.exceptions as (PlanningException & { id: string })[],
				nextPageToken: resp.page?.nextPageToken,
				totalCount: resp.page?.totalCount ?? 0,
			};
		},
		pageSize: 100,
		colToField: {
			exceptionType: "exception_type",
			productId: "product_id",
			createdAt: "created_at",
		},
	});

	// Bulk actions
	const handleAcknowledge = useCallback(() => {
		for (const id of selectedIds) {
			acknowledgeException.mutate({ id });
		}
		setSelectedIds([]);
	}, [selectedIds, acknowledgeException]);

	const handleResolve = useCallback(() => {
		for (const id of selectedIds) {
			resolveException.mutate({ id });
		}
		setSelectedIds([]);
	}, [selectedIds, resolveException]);

	const statusBar = useMemo(
		() => [
			{
				id: "count",
				content: `${totalRows} ${t("status_bar_rows")}`,
				align: "left" as const,
			},
			{
				id: "selected",
				content:
					selectedIds.length > 0
						? `${selectedIds.length} ${t("status_bar_selected")}`
						: "",
				align: "right" as const,
			},
		],
		[totalRows, selectedIds, t],
	);

	useWorkspacePage({ statusBar });

	// Keyboard shortcuts
	// useEffect(() => {
	//   const handler = (e: KeyboardEvent) => {
	//     if (e.key === 'a' && !e.ctrlKey && selectedIds.length > 0) handleAcknowledge();
	//     if (e.key === 'r' && !e.ctrlKey && selectedIds.length > 0) handleResolve();
	//   };
	//   window.addEventListener('keydown', handler);
	//   return () => window.removeEventListener('keydown', handler);
	// }, [selectedIds, handleAcknowledge, handleResolve]);

	return (
		<div className="flex h-full flex-col">
			<div className="flex items-center gap-2 px-4 py-2 shrink-0">
				<div className="relative flex-1">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("search_placeholder", { defaultValue: "Поиск..." })}
						onChange={(e) =>
							gridRef.current?.api?.setGridOption(
								"quickFilterText",
								e.target.value,
							)
						}
						className="pl-9 h-9"
					/>
				</div>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={handleAcknowledge}
					disabled={selectedIds.length === 0}
				>
					{t("action_acknowledge")}
				</Button>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => {
						for (const id of selectedIds) {
							dismissException.mutate({ id, reason: "Отклонено вручную" });
						}
						setSelectedIds([]);
					}}
					disabled={selectedIds.length === 0}
				>
					{t("action_dismiss")}
				</Button>
				<Button
					size="sm"
					className="h-9"
					onClick={handleResolve}
					disabled={selectedIds.length === 0}
				>
					{t("action_resolve")}
				</Button>
			</div>
			<div className="flex-1 ag-theme-quartz">
				<AgGridReact
					ref={gridRef}
					{...gridDefaults}
					columnDefs={columns}
					rowModelType="infinite"
					datasource={datasource}
					cacheBlockSize={100}
					maxBlocksInCache={20}
					rowSelection="multiple"
					suppressRowClickSelection
					onSelectionChanged={() => {
						const selected = gridRef.current?.api
							?.getSelectedRows()
							.map((r: { id: string }) => r.id);
						setSelectedIds(selected ?? []);
					}}
					onSortChanged={() => refreshGrid()}
					onFilterChanged={() => refreshGrid()}
					getRowId={(params) => params.data?.id}
				/>
			</div>
		</div>
	);
}
